import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ClipboardCheck, X } from "lucide-react";
import { toast } from "sonner";
import { openTaskWindow } from "@/components/tracker/TaskWindow";
import { confirm } from "@/components/tracker/confirm";

const EASE = [0.22, 1, 0.36, 1];

// Moving a task out of review (not to Done) is a withdraw and needs a reason.
// Resolves "" when no reason is needed, null when cancelled, otherwise the reason.
export async function askWithdrawReason(task, next, has, count = 1, user = null) {
  if (task?.status !== "REVIEW" || !next || next === "REVIEW" || next === "DONE" || !has?.("review")) return "";
  if (user && !["admin", "lead"].includes(user.role)) {
    toast.error("Only a project lead or an admin can take a task out of review");
    return null;
  }
  const r = await confirm({
    title: count > 1 ? `Withdraw ${count} tasks from review?` : "Withdraw from review?",
    message: `${count > 1 ? "They go" : "It goes"} back out of review and the reviewer is notified. Add a short reason — it's saved in the review history.`,
    confirmText: "Withdraw",
    tone: "neutral",
    input: { placeholder: "e.g. Sent by mistake, missing drawings…" },
  });
  return r || null;
}

let emit = null;
let resolver = null;

// Asks before sending unfinished work to review.
// Resolves "complete" (set 100% & send), "anyway" (send as partial) or null (cancel).
// Pass `has` so plans without the review workflow (Review is a plain status) skip the prompt.
export function guardReview({ progress, count = 1, has }) {
  if (progress >= 100 || (has && !has("review"))) return Promise.resolve("anyway");
  return new Promise((resolve) => {
    resolver = resolve;
    if (emit) emit({ progress, count });
    else resolve("anyway");
  });
}

// On plans with the review workflow a task needs a named reviewer before it can enter REVIEW.
export function needsReviewer(task, has) {
  if (!has?.("review") || task?.reviewer_id) return false;
  toast.info("Choose a reviewer to send this task for review");
  openTaskWindow(task.id);
  return true;
}

let emitDone = null;
let doneResolver = null;

// Asks what a task reaching 100% should do next: "done", "review" or null (cancel).
export function askCompletion({ count = 1, finalReview = false, plain = false } = {}) {
  return new Promise((resolve) => {
    doneResolver = resolve;
    if (emitDone) emitDone({ count, finalReview, plain });
    else resolve("done");
  });
}

// Asks Done vs Review for a task hitting 100% and saves via `save(body)`; false if cancelled.
// Without a reviewer it saves 100% first, then asks the user to pick one.
export async function completeTask(task, has, save) {
  const finalReview = (task?.review_history || []).at(-1)?.type === "approved_partial";
  const choice = await askCompletion({ finalReview, plain: !has?.("review") });
  if (!choice) return false;
  const pick = choice === "review" && has?.("review") && !task?.reviewer_id;
  await save(choice === "done" ? { progress: 100, status: "DONE" } : pick ? { progress: 100 } : { progress: 100, status: "REVIEW" });
  if (pick) needsReviewer(task, has);
  return true;
}

export const reachesCompletion = (task, progress) => progress === 100 && !["DONE", "REVIEW"].includes(task?.status);

export function CompletionHost() {
  const [opts, setOpts] = useState(null);

  useEffect(() => {
    emitDone = setOpts;
    return () => { emitDone = null; };
  }, []);

  const close = (val) => {
    setOpts(null);
    const r = doneResolver;
    doneResolver = null;
    if (r) r(val);
  };

  useEffect(() => {
    if (!opts) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      close(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [opts]);

  const many = opts && opts.count > 1;
  return createPortal(
    <AnimatePresence>
      {opts && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="completion-guard">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => close(null)} />
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-[#0d0f12] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]"
          >
            <div className="h-1 w-full bg-[#00FF66]" />
            <button onClick={() => close(null)} className="absolute right-4 top-5 text-white/40 hover:text-white" aria-label="Cancel" data-testid="completion-guard-close">
              <X className="h-4 w-4" />
            </button>
            <div className="p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#00FF66]/15 text-[#00FF66]">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <h3 className="font-display mt-4 text-xl font-bold text-white" data-testid="completion-guard-title">
                {many ? `${opts.count} tasks at 100%. What's next?` : "Task at 100%. What's next?"}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/55" data-testid="completion-guard-body">
                {opts.finalReview
                  ? "This task had a partial review approved, so it needs a final review now that it's complete."
                  : opts.plain
                    ? <>Mark it <span className="font-semibold text-[#A8E6CF]">done</span> now, or move it to the Review column to track that it&apos;s being checked.</>
                    : <>Mark it <span className="font-semibold text-[#A8E6CF]">done</span> now, or send it to a reviewer to sign off first.</>}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-white/[0.06] bg-white/[0.02] px-6 py-4">
              <button onClick={() => close(null)} className="rounded-full px-4 py-2 text-xs font-bold text-white/50 hover:text-white" data-testid="completion-guard-cancel">
                Cancel
              </button>
              <button onClick={() => close("review")} className="flex items-center gap-1.5 rounded-full border border-[#FFB020]/40 px-4 py-2 text-xs font-bold text-[#FFB020] transition-colors hover:bg-[#FFB020]/10" data-testid="completion-guard-review">
                <ClipboardCheck className="h-3.5 w-3.5" /> {opts.finalReview ? "Send for final review" : opts.plain ? "Move to Review" : "Send for review"}
              </button>
              {!opts.finalReview && <button autoFocus onClick={() => close("done")} className="rounded-full bg-[#00FF66] px-4 py-2 text-xs font-black text-black transition-transform hover:-translate-y-0.5" data-testid="completion-guard-done">
                Mark as done
              </button>}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export const isPartial = (t) => t?.status === "REVIEW" && typeof t.progress === "number" && t.progress < 100;

export function PartialTag({ progress, testid, className = "" }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border border-[#FFB020]/40 bg-[#FFB020]/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-[#FFB020] ${className}`}
      title="Sent to review before reaching 100%"
      data-testid={testid}
    >
      Partial · {progress}%
    </span>
  );
}

export function PartialDot({ progress, testid, className = "" }) {
  return (
    <span
      className={`pointer-events-auto inline-block h-2 w-2 rounded-full bg-[#FFB020] ring-2 ring-[#0A0A0A] ${className}`}
      title={`Partial review: sent at ${progress}%`}
      aria-label={`Partial review, sent at ${progress}%`}
      data-testid={testid}
    />
  );
}

export function ReviewGuardHost() {
  const [opts, setOpts] = useState(null);

  useEffect(() => {
    emit = setOpts;
    return () => { emit = null; };
  }, []);

  const close = (val) => {
    setOpts(null);
    const r = resolver;
    resolver = null;
    if (r) r(val);
  };

  useEffect(() => {
    if (!opts) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      close(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [opts]);

  const many = opts && opts.count > 1;
  return createPortal(
    <AnimatePresence>
      {opts && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="review-guard">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => close(null)} />
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.25, ease: EASE }}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-[#0d0f12] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)]"
          >
            <div className="h-1 w-full bg-white/5">
              <div className="h-full bg-[#FFB020]" style={{ width: `${many ? 100 : opts.progress}%` }} />
            </div>
            <button onClick={() => close(null)} className="absolute right-4 top-5 text-white/40 hover:text-white" aria-label="Cancel" data-testid="review-guard-close">
              <X className="h-4 w-4" />
            </button>
            <div className="p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFB020]/15 text-[#FFB020]">
                <ClipboardCheck className="h-5 w-5" />
              </span>
              <h3 className="font-display mt-4 text-xl font-bold text-white" data-testid="review-guard-title">
                {many ? `${opts.count} tasks aren't at 100% yet` : `This task is only at ${opts.progress}%`}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/55">
                Reviews usually happen once the work is complete. Is it ready for sign-off?
                If you send it anyway, the reviewer will see it tagged as a <span className="font-semibold text-[#FFB020]">partial</span> review.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-white/[0.06] bg-white/[0.02] px-6 py-4">
              <button onClick={() => close(null)} className="rounded-full px-4 py-2 text-xs font-bold text-white/50 hover:text-white" data-testid="review-guard-cancel">
                Cancel
              </button>
              <button onClick={() => close("anyway")} className="rounded-full border border-[#FFB020]/40 px-4 py-2 text-xs font-bold text-[#FFB020] transition-colors hover:bg-[#FFB020]/10" data-testid="review-guard-anyway">
                {many ? "Send as partial" : `Send at ${opts.progress}% anyway`}
              </button>
              <button autoFocus onClick={() => close("complete")} className="rounded-full bg-[#00FF66] px-4 py-2 text-xs font-black text-black transition-transform hover:-translate-y-0.5" data-testid="review-guard-complete">
                Set to 100% &amp; send
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
